import { toaster } from '@/components/ui/toaster';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import { planFeaturesQueryKey } from '@/hooks/queries/use-plan-features-query';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { isSuccessFeeTermsRefusal } from '@/libs/utils/success-fee-terms-refusal';
import type {
  ApiResponse,
  ChangeSubscriptionPlanRequest,
  OrganizationBillingContext,
  PlanChangeResponseData,
} from '@fluenceur/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';

const identifier = z
  .string()
  .min(1)
  .refine((value) => value === value.trim());
const date = z.string().datetime({ offset: true });
const httpsUrl = z
  .string()
  .url()
  .refine((value) => new URL(value).protocol === 'https:');
const invoiceUrl = httpsUrl.refine((value) => {
  const url = new URL(value);
  return (
    ['invoice.stripe.com', 'pay.stripe.com'].includes(url.hostname) &&
    !url.username &&
    !url.password &&
    !url.port
  );
});
const stripeFields = {
  channel: z.literal('stripe'),
  operationId: identifier,
  subscriptionId: identifier,
};
const legacySchema = z
  .object({
    confirmationUrl: httpsUrl,
    subscriptionId: identifier,
    isUpgrade: z.boolean(),
    effectiveDate: z.enum(['immediate', 'next_billing_cycle']),
  })
  .strict();
const resultSchema = z.discriminatedUnion('kind', [
  legacySchema.extend({ kind: z.literal('redirect'), channel: z.literal('shopify') }),
  z
    .object({
      ...stripeFields,
      kind: z.literal('applied'),
      planIdentifier: z.enum(['essential', 'growth']),
      effectiveAt: date,
    })
    .strict(),
  z
    .object({
      ...stripeFields,
      kind: z.literal('scheduled'),
      planIdentifier: z.enum(['essential', 'growth']),
      targetPlan: z.enum(['essential', 'growth']),
      effectiveAt: date,
    })
    .strict(),
  z
    .object({
      ...stripeFields,
      kind: z.literal('requires_action'),
      hostedInvoiceUrl: invoiceUrl,
      expiresAt: date,
    })
    .strict(),
  z
    .object({
      ...stripeFields,
      kind: z.literal('pending'),
      status: z.enum([
        'reserved',
        'processing',
        'pending',
        'uncertain',
        'needs_reconciliation',
        'expired',
        'superseded',
      ]),
      nextAttemptAt: date.nullable(),
    })
    .strict(),
]);
const requestSchema = z
  .object({
    planId: z.enum(['essential_monthly', 'growth_monthly']),
    expectedSubscriptionId: identifier.optional(),
  })
  .strict();
const readResult = (value: unknown): PlanChangeResponseData | null => {
  const legacy = legacySchema.safeParse(value);
  const result = resultSchema.safeParse(
    legacy.success ? { kind: 'redirect', channel: 'shopify', ...legacy.data } : value
  );
  return result.success ? result.data : null;
};
const needsCheck = (value: PlanChangeResponseData | null) =>
  value?.kind === 'requires_action' ||
  (value?.kind === 'pending' && !['expired', 'superseded'].includes(value.status));

/** A durable operation is read again; polling never repeats the financial POST. */
export function useChangeSubscriptionPlan({
  billingContext,
}: { billingContext?: OrganizationBillingContext } = {}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingActionScope();
  const [receipt, setReceipt] = useState<{
    target: typeof scope;
    result: PlanChangeResponseData;
  } | null>(null);
  const currentReceipt =
    receipt &&
    receipt.target.organizationId === scope.organizationId &&
    receipt.target.brandId === scope.brandId &&
    receipt.target.userId === scope.userId
      ? receipt.result
      : null;
  const context = readBillingContext(billingContext);
  const contextResult =
    context &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    context.capabilities.canReadBilling &&
    context.cardSetup.exemption !== 'impersonation'
      ? readResult(context.planChange)
      : null;
  const scopedContextResult =
    contextResult?.channel === 'stripe' &&
    contextResult.subscriptionId === context?.subscription?.id
      ? contextResult
      : null;
  const operation = currentReceipt ?? scopedContextResult;
  const canRead =
    !!scope.userId && !!scope.organizationId && !!scope.brandId && operation?.channel === 'stripe';

  const announceError = (error: unknown) => {
    if (error instanceof BillingTargetChangedError || isSuccessFeeTermsRefusal(error)) return;
    toaster.error({
      title: t('settings.subscription.changePlan.error'),
      description: getApiErrorMessage(error, t, 'settings.subscription.changePlan.error'),
    });
  };
  const refresh = async (result: PlanChangeResponseData, target: typeof scope) => {
    assertCurrent(target);
    await Promise.all([
      client.invalidateQueries({ queryKey: billingContextQueryKey(target), exact: true }),
      ...(result.kind === 'applied'
        ? [
            client.invalidateQueries({
              queryKey: [...SUBSCRIPTION_QUERY_KEY, target.brandId],
              exact: true,
            }),
            client.invalidateQueries({ queryKey: planFeaturesQueryKey, exact: true }),
          ]
        : []),
    ]);
    assertCurrent(target);
  };
  const mutation = useMutation<
    PlanChangeResponseData,
    AxiosError<ApiResponse> | Error,
    ChangeSubscriptionPlanRequest | string,
    typeof scope
  >({
    mutationKey: ['changeSubscriptionPlan', scope.userId, scope.organizationId, scope.brandId],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (input) => {
      assertCurrent(scope);
      if (!scope.userId || !scope.organizationId || !scope.brandId)
        throw new Error('No billing target');
      const payload = typeof input === 'string' ? { planId: input } : requestSchema.parse(input);
      const response = await api.post<ApiResponse<unknown>>(
        '/dashboard/subscription/change-plan',
        payload
      );
      assertCurrent(scope);
      const result = response.data.success === true ? readResult(response.data.data) : null;
      if (
        !result ||
        (result.channel === 'stripe' &&
          (('expectedSubscriptionId' in payload &&
            payload.expectedSubscriptionId !== undefined &&
            result.subscriptionId !== payload.expectedSubscriptionId) ||
            (result.kind === 'applied' && `${result.planIdentifier}_monthly` !== payload.planId) ||
            (result.kind === 'scheduled' && `${result.targetPlan}_monthly` !== payload.planId)))
      )
        throw new Error('Invalid plan change response');
      return result;
    },
    onSuccess: async (result, _input, target) => {
      if (!target) throw new Error('Missing billing target');
      assertCurrent(target);
      await refresh(result, target);
      setReceipt({ target, result });
      if (result.kind === 'redirect') {
        toaster.success({
          title: t('settings.subscription.changePlan.success.title'),
          description: t('settings.subscription.changePlan.success.description'),
        });
        window.location.href = result.confirmationUrl;
      } else if (result.kind === 'applied' || result.kind === 'scheduled') {
        toaster.success({
          title: t(
            result.kind === 'applied' ? 'planChange.applied.title' : 'planChange.scheduled.title'
          ),
        });
      }
    },
    onError: announceError,
  });

  const status = useQuery<PlanChangeResponseData>({
    queryKey: [
      'subscriptionPlanChange',
      scope.userId,
      scope.organizationId,
      scope.brandId,
      operation?.channel === 'stripe' ? operation.operationId : null,
    ],
    enabled: canRead && needsCheck(operation),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (needsCheck(query.state.data ?? operation) ? 5000 : false),
    queryFn: async ({ signal }): Promise<PlanChangeResponseData> => {
      try {
        assertCurrent(scope);
        if (!canRead || operation?.channel !== 'stripe')
          throw new Error('No authorized plan change operation');
        const response = await api.get<ApiResponse<unknown>>(
          `/dashboard/subscription/plan-change/${encodeURIComponent(operation.operationId)}`,
          { signal }
        );
        assertCurrent(scope);
        const result = response.data.success === true ? readResult(response.data.data) : null;
        if (
          !result ||
          result.channel !== 'stripe' ||
          result.operationId !== operation.operationId ||
          result.subscriptionId !== operation.subscriptionId
        )
          throw new Error('Invalid plan change status');
        if (result.kind === 'applied' || result.kind === 'scheduled') await refresh(result, scope);
        assertCurrent(scope);
        setReceipt({ target: scope, result });
        return result;
      } catch (error) {
        if (!signal.aborted) announceError(error);
        throw error;
      }
    },
  });
  return {
    ...mutation,
    planChangeResult: operation,
    checkPlanChange: () => status.refetch(),
    isCheckingPlanChange: status.isFetching,
    planChangeError: status.error,
  };
}
